import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabaseClient';
import { useAuth } from '../../auth/AuthContext';
import '../../components/Menu/Menu.css';
import BurgerRain from '../../components/BurgerRain/BurgerRain';

// Backend modellerine uygun tipler
interface MainCategory { 
  id: number; 
  nameTr: string; 
  nameEn: string;
  isActive: boolean;
  orderIndex: number;
}

interface SubCategory { 
  id: number; 
  nameTr: string; 
  nameEn: string;
  mainCategoryId: number; 
  mainCategory?: MainCategory; 
  isActive: boolean;
  orderIndex: number;
}

interface Product {
  id: number;
  nameTr: string;
  nameEn: string;
  descriptionTr: string;
  descriptionEn: string;
  flavorTr: string;
  flavorEn: string;
  price: string;
  mainCategoryId?: number;
  subCategoryId?: number;
  mainCategoryNameTr?: string;
  mainCategoryNameEn?: string;
  subCategoryNameTr?: string;
  subCategoryNameEn?: string;
  isActive: boolean;
  allergenInfoTr?: string;
  allergenInfoEn?: string;
  isNew: boolean;
  imageUrl?: string;
  orderIndex: number;
}

interface MontlyCalendarImage {
  id: number;
  imageUrl: string;
}

interface User {
  id: number;
  username: string;
  passwordHash: string;
  role: 'SuperAdmin' | 'Admin' | 'User';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

// Supabase client paylaşılan modülden alınır

const REST_BASE = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1`;
const REST_HEADERS: Record<string, string> = {
  apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
  Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}`,
};
// Geçiş sürecinde eski sabitleri Supabase REST'e yönlendiriyoruz
// API_BASE removed - using REST_BASE for Supabase REST
const BACKEND_BASE = '';

// Authenticated write headers (Bearer access_token)
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token || '';
  return {
    apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
    Authorization: `Bearer ${token}`,
  };
};

const getAuthJsonHeaders = async (): Promise<Record<string, string>> => {
  const base = await getAuthHeaders();
  return { ...base, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' };
};

// Modern renkler
const primary = '#6366f1';
const secondary = '#8b5cf6';
const accent = '#06b6d4';
const success = '#10b981';
const warning = '#f59e0b';
const danger = '#ef4444';
const light = '#f8fafc';
const dark = '#1e293b';
const gray = '#64748b';

// Dil renkleri
const trColor = '#dc2626'; // Kırmızı
const enColor = '#1e40af'; // Mavi

const MenuAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  
  // Login state
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // BurgerRain yönetimi
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
  const [rainImages, setRainImages] = useState<Record<number, string>>({}); // day(1-31) -> url
  const [previewDay, setPreviewDay] = useState<number | null>(null);
  const [isRainOpen, setIsRainOpen] = useState<boolean>(false);
  
  // MontlyCalendarImage yönetimi
  const [montlyCalendarImages, setMontlyCalendarImages] = useState<MontlyCalendarImage[]>([]);
  const [showMontlyCalendarForm, setShowMontlyCalendarForm] = useState(false);
  const [montlyCalendarForm, setMontlyCalendarForm] = useState({ imageUrl: '' });
  const [editingMontlyCalendar, setEditingMontlyCalendar] = useState<MontlyCalendarImage | null>(null);
  const [isMontlyCalendarOpen, setIsMontlyCalendarOpen] = useState<boolean>(false);
  
  // Seçili kategoriler
  const [selectedMainCategory, setSelectedMainCategory] = useState<MainCategory | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  
  // Form modalları
  const [showMainCategoryForm, setShowMainCategoryForm] = useState(false);
  const [showSubCategoryForm, setShowSubCategoryForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  
  // Form verileri
  const [mainCategoryForm, setMainCategoryForm] = useState({ 
    nameTr: '', 
    nameEn: '', 
    isActive: true 
  });
  const [subCategoryForm, setSubCategoryForm] = useState({ 
    nameTr: '', 
    nameEn: '', 
    mainCategoryId: '', 
    isActive: true 
  });
  const [productForm, setProductForm] = useState({
    nameTr: '', 
    nameEn: '',
    descriptionTr: '', 
    descriptionEn: '',
    flavorTr: '', 
    flavorEn: '',
    price: '', 
    mainCategoryId: '', 
    subCategoryId: '', 
    isActive: true, 
    allergenInfoTr: '',
    allergenInfoEn: '',
    isNew: false,
    imageUrl: '',
  });
  
  // Edit modları
  const [editingMainCategory, setEditingMainCategory] = useState<MainCategory | null>(null);
  const [editingSubCategory, setEditingSubCategory] = useState<SubCategory | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Kullanıcı yönetimi
  // Kullanıcı yönetimi Supabase'e taşındı; yerel state kaldırıldı
  const users: User[] = [];
  const showUserForm = false;
  const isUserManagementOpen = false;
  const editingUser: User | null = null;
  const userForm = { username: '', password: '', role: 'User' as 'SuperAdmin' | 'Admin' | 'User', isActive: true };
  const showPasswords: Record<number, boolean> = {};
  const openUserForm = (_user?: User) => {};
  const togglePasswordVisibility = (_userId: number) => {};
  const handleUserSubmit = (_e: React.FormEvent) => { _e.preventDefault(); };
  const deleteUser = (_id: number) => {};
  const getRoleDisplayName = (_role: string) => 'User';
  const getRoleColor = (_role: string) => '#64748b';
  const currentUser: any = null;
  const setCurrentUser = (_val: any) => {};

  // Login state kaldırıldı, Supabase ile yönetilecek – derleme için stub değerler
  const loginForm = { username: '', password: '' };
  const setLoginForm = (_updater: any) => {};
  const loginError: string | null = null;
  const setLoginError = (_val: any) => {};

  // Veri çekme
  const fetchAll = async () => {
    setLoading(true);
    try {
      const start = `${currentYear}-${String(currentMonth).padStart(2,'0')}-01`;
      const endMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      const endYear = currentMonth === 12 ? currentYear + 1 : currentYear;
      const end = `${endYear}-${String(endMonth).padStart(2,'0')}-01`;

      const [productsRes, mainCategoriesRes, subCategoriesRes, rainImagesRes, montlyCalendarRes] = await Promise.all([
        fetch(`${REST_BASE}/products?select=*&order=orderindex`, { headers: REST_HEADERS }),
        fetch(`${REST_BASE}/maincategories?select=*&order=orderindex`, { headers: REST_HEADERS }),
        fetch(`${REST_BASE}/subcategories?select=*&order=maincategoryid,orderindex`, { headers: REST_HEADERS }),
        fetch(`${REST_BASE}/rainimages?select=*&date=gte.${start}&date=lt.${end}&order=date`, { headers: REST_HEADERS }),
        fetch(`${REST_BASE}/montlycalendarimages?select=*&order=id.desc&limit=1`, { headers: REST_HEADERS })
      ]);

      const [productsRaw, mainCategoriesRaw, subCategoriesRaw, rainImagesRaw, montlyCalendarRaw] = await Promise.all([
        productsRes.json(),
        mainCategoriesRes.json(),
        subCategoriesRes.json(),
        rainImagesRes.json(),
        montlyCalendarRes.json()
      ]);

      const normProduct = (p: any) => ({
        id: p.id,
        nameTr: p.name_tr,
        nameEn: p.name_en,
        descriptionTr: p.description_tr,
        descriptionEn: p.description_en,
        flavorTr: p.flavor_tr,
        flavorEn: p.flavor_en,
        price: p.price,
        orderIndex: p.orderindex ?? 0,
        mainCategoryId: p.maincategoryid,
        subCategoryId: p.subcategoryid,
        isActive: p.is_active ?? true,
        allergenInfoTr: p.allergen_info_tr,
        allergenInfoEn: p.allergen_info_en,
        isNew: p.is_new ?? false,
        imageUrl: p.image_url,
      });
      const normMain = (m: any) => ({ 
        id: m.id,
        nameTr: m.name_tr,
        nameEn: m.name_en,
        orderIndex: m.orderindex ?? 0,
        isActive: m.is_active ?? true
      });
      const normSub = (s: any) => ({ 
        id: s.id,
        nameTr: s.name_tr,
        nameEn: s.name_en,
        orderIndex: s.orderindex ?? 0,
        mainCategoryId: s.maincategoryid,
        isActive: s.is_active ?? true
      });

      const products = Array.isArray(productsRaw) ? productsRaw.map(normProduct) : [];
      const mainCategories = Array.isArray(mainCategoriesRaw) ? mainCategoriesRaw.map(normMain) : [];
      const subCategories = Array.isArray(subCategoriesRaw) ? subCategoriesRaw.map(normSub) : [];
      const rainImages = Array.isArray(rainImagesRaw) ? rainImagesRaw : [];
      const montlyCalendarImages = Array.isArray(montlyCalendarRaw)
        ? montlyCalendarRaw.map((m: any) => ({ id: m.id ?? m.Id, imageUrl: m.image_url ?? m.ImageUrl ?? '' }))
        : [];

      // orderIndex'e göre sırala
      const sortedProducts = products.sort((a: Product, b: Product) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
      const sortedMainCategories = mainCategories.sort((a: MainCategory, b: MainCategory) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
      const sortedSubCategories = subCategories.sort((a: SubCategory, b: SubCategory) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))

      setProducts(sortedProducts);
      setMainCategories(sortedMainCategories);
      setSubCategories(sortedSubCategories);
      setMontlyCalendarImages(montlyCalendarImages);

      // Rain images'ı map'e çevir
      const map: Record<number, string> = {};
      rainImages.forEach((item: { date: string; image_url: string }) => {
        const day = new Date(item.date).getDate();
        map[day] = item.image_url;
      });
      setRainImages(map);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  // Rain görseli yükleme/helper
  const uploadRainImage = async (file: File): Promise<string> => {
    return await uploadToStorage(file, 'rain-images', 'daily');
  };

  const saveRainImage = async (day: number, imageUrl: string | null) => {
    if (imageUrl) {
      try {
        const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        // Ensure single record per day: delete existing by date, then insert
        await fetch(`${REST_BASE}/rainimages?date=eq.${dateStr}`, { method: 'DELETE', headers: await getAuthHeaders() });
        const res = await fetch(`${REST_BASE}/rainimages`, { method: 'POST', headers: await getAuthJsonHeaders(), body: JSON.stringify({ date: dateStr, image_url: imageUrl }) });
        if (!res.ok) throw new Error('Kaydetme başarısız');
        await fetchAll();
      } catch (err: any) {
        setError(err.message);
      }
    } else {
      try {
        const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const res = await fetch(`${REST_BASE}/rainimages?date=eq.${dateStr}`, { headers: REST_HEADERS });
        if (res.ok) {
          const data = await res.json();
          // Delete all records for the date to guarantee removal
          await fetch(`${REST_BASE}/rainimages?date=eq.${dateStr}`, { method: 'DELETE', headers: await getAuthHeaders() });
          await fetchAll();
        }
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  // MontlyCalendarImage CRUD işlemleri
  const uploadMontlyCalendarImage = async (file: File): Promise<string> => {
    return await uploadToStorage(file, 'monthly-calendar', 'images');
  };

  const saveMontlyCalendarImage = async (file: File) => {
    try {
      const imageUrl = await uploadMontlyCalendarImage(file);
      await fetchAll();
      setShowMontlyCalendarForm(false);
      setMontlyCalendarForm({ imageUrl: '' });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateMontlyCalendarImage = async (id: number, file: File) => {
    try {
      const imageUrl = await uploadMontlyCalendarImage(file);
      
      const res = await fetch(`${REST_BASE}/montlycalendarimages?id=eq.${id}`, { method: 'PATCH', headers: await getAuthJsonHeaders(), body: JSON.stringify({ image_url: imageUrl }) });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Güncelleme başarısız: ${errorText}`);
      }
      
      await fetchAll();
      setShowMontlyCalendarForm(false);
      setMontlyCalendarForm({ imageUrl: '' });
      setEditingMontlyCalendar(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteMontlyCalendarImage = async (id: number) => {
    if (window.confirm('Bu aylık takvim görselini silmek istediğinizden emin misiniz?')) {
      try {
        const res = await fetch(`${REST_BASE}/montlycalendarimages?id=eq.${id}`, {
          method: 'DELETE',
          headers: await getAuthHeaders()
        });
        if (!res.ok) throw new Error('Silme başarısız');
        await fetchAll();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleMontlyCalendarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fileInput = document.getElementById('montlyCalendarFile') as HTMLInputElement;
    const file = fileInput?.files?.[0];
    
    if (!file) {
      setError('Lütfen bir dosya seçin.');
      return;
    }

    if (editingMontlyCalendar) {
      updateMontlyCalendarImage(editingMontlyCalendar.id, file);
    } else {
      saveMontlyCalendarImage(file);
    }
  };

  const openMontlyCalendarForm = (image?: MontlyCalendarImage) => {
    if (image) {
      setEditingMontlyCalendar(image);
      setMontlyCalendarForm({ imageUrl: image.imageUrl });
    } else {
      setEditingMontlyCalendar(null);
      setMontlyCalendarForm({ imageUrl: '' });
    }
    setShowMontlyCalendarForm(true);
  };

  // Login kontrolü kaldırıldı; sayfa yüklenince verileri çek
  useEffect(() => {
          fetchAll();
  }, []);

  // Veriler yüklendikten sonra ilk ana kategoriyi otomatik seç
  useEffect(() => {
    if (mainCategories.length > 0 && !selectedMainCategory) {
      setSelectedMainCategory(mainCategories[0]);
    }
  }, [mainCategories, selectedMainCategory]);

  // Ana kategori seçildiğinde alt kategorileri güncelle
  useEffect(() => {
    if (selectedMainCategory) {
      const filteredSubs = subCategories.filter(sc => sc.mainCategoryId === selectedMainCategory.id);
      if (filteredSubs.length > 0) {
        setSelectedSubCategory(filteredSubs[0]);
      } else {
        setSelectedSubCategory(null);
      }
    }
  }, [selectedMainCategory, subCategories]);

  // CRUD işlemleri
  // --- Sıralama yardımcıları ---
  const buildReorderItems = (idsInOrder: number[]) => idsInOrder.map((id, index) => ({ id, orderIndex: index }));

  const saveMainCategoryOrder = async (ordered: MainCategory[]) => {
    // Update each item's orderIndex
    for (let i = 0; i < ordered.length; i++) {
      const item = ordered[i];
      await fetch(`${REST_BASE}/maincategories?id=eq.${item.id}`, { method: 'PATCH', headers: await getAuthJsonHeaders(), body: JSON.stringify({ orderindex: i }) });
    }
  };

  const saveSubCategoryOrder = async (mainCategoryId: number, ordered: SubCategory[]) => {
    // Update each item's orderIndex
    for (let i = 0; i < ordered.length; i++) {
      const item = ordered[i];
      await fetch(`${REST_BASE}/subcategories?id=eq.${item.id}`, { method: 'PATCH', headers: await getAuthJsonHeaders(), body: JSON.stringify({ orderindex: i }) });
    }
  };

  const saveProductOrderByMainCategory = async (mainCategoryId: number, ordered: Product[]) => {
    // Update each item's orderIndex
    for (let i = 0; i < ordered.length; i++) {
      const item = ordered[i];
      await fetch(`${REST_BASE}/products?id=eq.${item.id}`, { method: 'PATCH', headers: await getAuthJsonHeaders(), body: JSON.stringify({ orderindex: i }) });
    }
  };

  const saveProductOrderBySubCategory = async (subCategoryId: number, ordered: Product[]) => {
    // Update each item's orderIndex
    for (let i = 0; i < ordered.length; i++) {
      const item = ordered[i];
      await fetch(`${REST_BASE}/products?id=eq.${item.id}`, { method: 'PATCH', headers: await getAuthJsonHeaders(), body: JSON.stringify({ orderindex: i }) });
    }
  };

  function moveItem<T>(arr: T[], fromIndex: number, toIndex: number) {
    const copy = [...arr];
    const [spliced] = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, spliced);
    return copy;
  }

  const moveMainCategory = async (id: number, direction: 'up' | 'down') => {
    const index = mainCategories.findIndex(c => c.id === id);
    if (index === -1) return;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= mainCategories.length) return;
    const ordered = moveItem(mainCategories, index, target);
    setMainCategories(ordered);
    await saveMainCategoryOrder(ordered);
    await fetchAll();
  };

  const moveSubCategory = async (id: number, direction: 'up' | 'down') => {
    if (!selectedMainCategory) return;
    const scoped = subCategories.filter(s => s.mainCategoryId === selectedMainCategory.id);
    const index = scoped.findIndex(s => s.id === id);
    if (index === -1) return;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= scoped.length) return;
    const newScoped = moveItem(scoped, index, target);
    // merge back into full list
    const rest = subCategories.filter(s => s.mainCategoryId !== selectedMainCategory.id);
    const newAll = [...rest, ...newScoped];
    setSubCategories(newAll);
    await saveSubCategoryOrder(selectedMainCategory.id, newScoped);
    await fetchAll();
  };

  const moveProduct = async (id: number, direction: 'up' | 'down') => {
    const list = filteredProducts;
    const index = list.findIndex(p => p.id === id);
    if (index === -1) return;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    const newList = moveItem(list, index, target);
    // merge into all products, preserving items not in current filter
    const rest = products.filter(p => !list.some(lp => lp.id === p.id));
    const newAll = [...rest, ...newList];
    setProducts(newAll);
    if (selectedSubCategory) {
      await saveProductOrderBySubCategory(selectedSubCategory.id, newList);
    } else if (selectedMainCategory) {
      await saveProductOrderByMainCategory(selectedMainCategory.id, newList);
    }
    await fetchAll();
  };

  const handleMainCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const method = editingMainCategory ? 'PATCH' : 'POST';
      const url = editingMainCategory 
        ? `${REST_BASE}/maincategories?id=eq.${editingMainCategory.id}` 
        : `${REST_BASE}/maincategories`;
      const body = {
        name_tr: mainCategoryForm.nameTr,
        name_en: mainCategoryForm.nameEn,
        orderindex: editingMainCategory ? undefined : mainCategories.length
      };
      const res = await fetch(url, { method, headers: await getAuthJsonHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Ana kategori kaydedilemedi');
      }
      setShowMainCategoryForm(false);
      setEditingMainCategory(null);
      setMainCategoryForm({ nameTr: '', nameEn: '', isActive: true });
      fetchAll();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  const handleSubCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const method = editingSubCategory ? 'PATCH' : 'POST';
      const url = editingSubCategory 
        ? `${REST_BASE}/subcategories?id=eq.${editingSubCategory.id}` 
        : `${REST_BASE}/subcategories`;
      const body = { 
        name_tr: subCategoryForm.nameTr, 
        name_en: subCategoryForm.nameEn, 
        maincategoryid: Number(subCategoryForm.mainCategoryId),
        orderindex: editingSubCategory ? undefined : subCategories.filter(s => s.mainCategoryId === Number(subCategoryForm.mainCategoryId)).length
      };
      const res = await fetch(url, { method, headers: await getAuthJsonHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Alt kategori kaydedilemedi');
      }
      setShowSubCategoryForm(false);
      setEditingSubCategory(null);
      setSubCategoryForm({ nameTr: '', nameEn: '', mainCategoryId: '', isActive: true });
      fetchAll();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const method = editingProduct ? 'PATCH' : 'POST';
      const url = editingProduct 
        ? `${REST_BASE}/products?id=eq.${editingProduct.id}` 
        : `${REST_BASE}/products`;
      const body = {
        name_tr: productForm.nameTr,
        name_en: productForm.nameEn,
        description_tr: productForm.descriptionTr,
        description_en: productForm.descriptionEn,
        flavor_tr: productForm.flavorTr,
        flavor_en: productForm.flavorEn,
        price: productForm.price,
        maincategoryid: productForm.mainCategoryId ? Number(productForm.mainCategoryId) : null,
        subcategoryid: productForm.subCategoryId ? Number(productForm.subCategoryId) : null,
        is_active: productForm.isActive,
        allergen_info_tr: productForm.allergenInfoTr,
        allergen_info_en: productForm.allergenInfoEn,
        is_new: productForm.isNew,
        image_url: productForm.imageUrl || null,
        orderindex: editingProduct ? undefined : products.filter(p => p.mainCategoryId === Number(productForm.mainCategoryId)).length
      };
      const res = await fetch(url, { method, headers: await getAuthJsonHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Ürün kaydedilemedi');
      }
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({ nameTr: '', nameEn: '', descriptionTr: '', descriptionEn: '', flavorTr: '', flavorEn: '', price: '', mainCategoryId: '', subCategoryId: '', isActive: true, allergenInfoTr: '', allergenInfoEn: '', isNew: false, imageUrl: '' });
      fetchAll();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  // Çok satırlı ad alanları için: Enter engellenir, Shift+Enter yeni satır
  const handleMultilineKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  };

  // Form açma işlemleri
  const openMainCategoryForm = (category?: MainCategory) => {
    if (category) {
      setEditingMainCategory(category);
      setMainCategoryForm({ nameTr: category.nameTr, nameEn: category.nameEn, isActive: category.isActive });
    } else {
      setEditingMainCategory(null);
      setMainCategoryForm({ nameTr: '', nameEn: '', isActive: true });
    }
    setShowMainCategoryForm(true);
  };

  const openSubCategoryForm = (subCategory?: SubCategory) => {
    if (subCategory) {
      setEditingSubCategory(subCategory);
      setSubCategoryForm({ nameTr: subCategory.nameTr, nameEn: subCategory.nameEn, mainCategoryId: String(subCategory.mainCategoryId), isActive: subCategory.isActive });
    } else {
      setEditingSubCategory(null);
      setSubCategoryForm({ nameTr: '', nameEn: '', mainCategoryId: selectedMainCategory ? String(selectedMainCategory.id) : '', isActive: true });
    }
    setShowSubCategoryForm(true);
  };

  const openProductForm = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      // Bazı ürünlerde mainCategoryId boş olabilir; subCategoryId'den türetelim
      let derivedMainCategoryId = product.mainCategoryId ? String(product.mainCategoryId) : '';
      if (!derivedMainCategoryId && product.subCategoryId) {
        const sc = subCategories.find(s => s.id === product.subCategoryId);
        if (sc) derivedMainCategoryId = String(sc.mainCategoryId);
      }
      setProductForm({
        nameTr: product.nameTr,
        nameEn: product.nameEn,
        descriptionTr: product.descriptionTr,
        descriptionEn: product.descriptionEn,
        flavorTr: product.flavorTr,
        flavorEn: product.flavorEn,
        price: product.price,
        mainCategoryId: derivedMainCategoryId,
        subCategoryId: product.subCategoryId ? String(product.subCategoryId) : '',
        isActive: product.isActive,
        allergenInfoTr: product.allergenInfoTr || '',
        allergenInfoEn: product.allergenInfoEn || '',
        isNew: product.isNew,
        imageUrl: product.imageUrl || '',
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        nameTr: '', nameEn: '', descriptionTr: '', descriptionEn: '', flavorTr: '', flavorEn: '', price: '',
        mainCategoryId: selectedMainCategory ? String(selectedMainCategory.id) : '',
        subCategoryId: selectedSubCategory ? String(selectedSubCategory.id) : '',
        isActive: true, allergenInfoTr: '', allergenInfoEn: '', isNew: false, imageUrl: ''
      });
    }
    setShowProductForm(true);
  };

  // Silme işlemleri
  const handleMainCategoryDelete = async (id: number) => {
    if (!window.confirm('Bu ana kategoriyi silmek istediğinize emin misiniz?')) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${REST_BASE}/maincategories?id=eq.${id}`, { method: 'DELETE', headers: await getAuthHeaders() });
      if (!res.ok) throw new Error('Silinemedi');
      fetchAll();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  const handleSubCategoryDelete = async (id: number) => {
    // Önce bu alt kategoriye bağlı ürün sayısını öğrenelim
    try {
      const productsRes = await fetch(`${REST_BASE}/products?select=id&subcategoryid=eq.${id}`, { headers: await getAuthHeaders() });
      const productsInSub: Array<{ id: number }> = productsRes.ok ? await productsRes.json() : [];
      const count = productsInSub.length;

      const choice = window.prompt(
        `Alt kategoriyi silmek üzeresiniz.\nBu alt kategoriye bağlı ${count} ürün var.\n\nSeçenekler:\n- "1": Sadece alt kategoriyi sil (ürünler ana kategorinin altına düşer)\n- "2": Ürünleriyle beraber sil`,
        '1'
      );
      if (choice !== '1' && choice !== '2') return;

      setLoading(true);
      setError(null);
      try {
        if (choice === '2') {
          if (count > 0) {
            const delProds = await fetch(`${REST_BASE}/products?subcategoryid=eq.${id}`, { method: 'DELETE', headers: await getAuthHeaders() });
            if (!delProds.ok) throw new Error('Ürünler silinemedi');
          }
        } else {
          const upd = await fetch(`${REST_BASE}/products?subcategoryid=eq.${id}`, { method: 'PATCH', headers: await getAuthJsonHeaders(), body: JSON.stringify({ subcategoryid: null }) });
          if (!upd.ok) {
            const t = await upd.text();
            throw new Error(`Ürünler taşınamadı: ${t}`);
          }
        }
        const delSub = await fetch(`${REST_BASE}/subcategories?id=eq.${id}`, { method: 'DELETE', headers: await getAuthHeaders() });
        if (!delSub.ok) throw new Error('Alt kategori silinemedi');
        await fetchAll();
      } catch (err: any) {
        setError(err.message);
      } finally { setLoading(false); }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleProductDelete = async (id: number) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${REST_BASE}/products?id=eq.${id}`, { method: 'DELETE', headers: await getAuthHeaders() });
      if (!res.ok) throw new Error('Silinemedi');
      fetchAll();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  // Filtrelenmiş veriler
  const filteredSubCategories = selectedMainCategory 
    ? subCategories.filter(sc => sc.mainCategoryId === selectedMainCategory.id)
    : [];

  const filteredProducts = selectedMainCategory
    ? selectedSubCategory
      ? products.filter(p => p.subCategoryId === selectedSubCategory.id)  // Alt kategori seçiliyse sadece o alt kategori
      : products.filter(p => p.mainCategoryId === selectedMainCategory.id)  // Alt kategori seçili değilse ana kategorinin tüm ürünleri
    : [];

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // Kullanıcı yönetimi CRUD işlemleri
  // const saveUser = async (...) => { ... }
  // const updateUser = async (...) => { ... }
  // const deleteUser = async (...) => { ... }
  // const changeUserPassword = async (...) => { ... }
  // const handleUserSubmit = async (...) => { ... }
  // const openUserForm = (user?: User) => { ... }
  // const togglePasswordVisibility = (userId: number) => { ... }
  // const getRoleDisplayName = (role: string) => { ... }
  // const getRoleColor = (role: string) => { ... }
  const canEdit = () => {
    return true;
  };

  // Login işlemleri
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    
    try {
      // TODO: Implement Supabase Auth login
      // For now, just set as logged in
      setIsLoggedIn(true);
      setLoginForm({ username: '', password: '' });
      await fetchAll();
    } catch (err: any) {
      setLoginError('Bağlantı hatası: ' + err.message);
    }
  };

  // Supabase Storage upload functions
  const uploadToStorage = async (file: File, bucket: string, path: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${path}/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file);
    
    if (error) throw new Error(`Upload failed: ${error.message}`);
    
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(fileName);
    
    return publicUrl;
  };

  return (
    <div className="menu-admin-container" style={{ minHeight: '100vh', background: light, padding: '20px' }}>
      {/* Login kaldırıldı: Supabase Auth entegrasyonu sonrası geri eklenebilir */}
      {isLoggedIn && (
        <>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        maxWidth: '1400px', 
        margin: '0 auto 32px',
        padding: '0 20px'
      }}>
            <div>
        <h1 style={{ fontSize: '2.5rem', color: dark, fontWeight: 'bold', margin: 0 }}>
          Menü Yönetim Paneli
        </h1>
              <div style={{ 
                marginTop: '8px', 
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)', 
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {/* Kullanıcı adı/rol gösterimi kaldırıldı */}
              </div>
            </div>
        <button
          onClick={handleLogout}
          style={{
            background: danger,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '1rem' }}>🚪</span>
          Çıkış Yap
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ display: 'inline-block', width: '40px', height: '40px', border: `4px solid ${gray}`, borderTop: `4px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
      )}
      {error && (
        <div style={{ background: danger, color: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center', margin: '20px auto', maxWidth: '600px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Ana Kategoriler */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: dark, margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Ana Kategoriler</h2>
            {canEdit() && (
              <button 
                onClick={() => openMainCategoryForm()}
                style={{ 
                  background: primary, 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>+</span> Ekle
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {mainCategories.map((category, idx) => (
              <div 
                key={category.id}
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px',
                  background: selectedMainCategory?.id === category.id ? `${primary}10` : 'transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: selectedMainCategory?.id === category.id ? `1px solid ${primary}` : '1px solid transparent',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSelectedMainCategory(category)}
              >
                <span style={{ 
                  color: dark, 
                  fontWeight: selectedMainCategory?.id === category.id ? '600' : '400',
                  fontSize: '0.95rem'
                }}>
                  {category.nameTr} ({category.nameEn})
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {canEdit() && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveMainCategory(category.id, 'up'); }}
                        disabled={idx === 0}
                        title="Yukarı Taşı"
                        style={{ background: 'transparent', border: '1px solid #e5e7eb', padding: '4px 6px', borderRadius: 6, color: gray }}
                      >⬆️</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveMainCategory(category.id, 'down'); }}
                        disabled={idx === mainCategories.length - 1}
                        title="Aşağı Taşı"
                        style={{ background: 'transparent', border: '1px solid #e5e7eb', padding: '4px 6px', borderRadius: 6, color: gray }}
                      >⬇️</button>
                    </div>
                  )}
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: category.isActive ? success : danger,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: category.isActive ? `${success}20` : `${danger}20`
                  }}>
                    {category.isActive ? 'Aktif' : 'Pasif'}
                  </span>
                  {canEdit() && (
                    <>
                      <button 
                        onClick={(e) => { e.stopPropagation(); openMainCategoryForm(category); }}
                        style={{ 
                          background: 'transparent', 
                          color: accent, 
                          border: 'none', 
                          borderRadius: '6px', 
                          padding: '6px',
                          cursor: 'pointer',
                          fontSize: '1rem'
                        }}
                        title="Düzenle"
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleMainCategoryDelete(category.id); }}
                        style={{ 
                          background: 'transparent', 
                          color: danger, 
                          border: 'none', 
                          borderRadius: '6px', 
                          padding: '6px',
                          cursor: 'pointer',
                          fontSize: '1rem'
                        }}
                        title="Sil"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alt Kategoriler */}
        {selectedMainCategory && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: dark, margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Alt Kategoriler</h2>
              {canEdit() && (
                <button 
                  onClick={() => openSubCategoryForm()}
                  style={{ 
                    background: primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>+</span> Ekle
                </button>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredSubCategories.map((subCategory, idx) => (
                <div 
                  key={subCategory.id}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '12px',
                    background: selectedSubCategory?.id === subCategory.id ? `${primary}10` : 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: selectedSubCategory?.id === subCategory.id ? `1px solid ${primary}` : '1px solid transparent',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setSelectedSubCategory(subCategory)}
                >
                  <span style={{ 
                    color: dark, 
                    fontWeight: selectedSubCategory?.id === subCategory.id ? '600' : '400',
                    fontSize: '0.95rem'
                  }}>
                    {subCategory.nameTr} ({subCategory.nameEn})
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {canEdit() && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveSubCategory(subCategory.id, 'up'); }}
                          disabled={idx === 0}
                          title="Yukarı Taşı"
                          style={{ background: 'transparent', border: '1px solid #e5e7eb', padding: '4px 6px', borderRadius: 6, color: gray }}
                        >⬆️</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveSubCategory(subCategory.id, 'down'); }}
                          disabled={idx === filteredSubCategories.length - 1}
                          title="Aşağı Taşı"
                          style={{ background: 'transparent', border: '1px solid #e5e7eb', padding: '4px 6px', borderRadius: 6, color: gray }}
                        >⬇️</button>
                      </div>
                    )}
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: subCategory.isActive ? success : danger,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: subCategory.isActive ? `${success}20` : `${danger}20`
                    }}>
                      {subCategory.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                    {canEdit() && (
                      <>
                        <button 
                          onClick={(e) => { e.stopPropagation(); openSubCategoryForm(subCategory); }}
                          style={{ 
                            background: 'transparent', 
                            color: accent, 
                            border: 'none', 
                            borderRadius: '6px', 
                            padding: '6px',
                            cursor: 'pointer',
                            fontSize: '1rem'
                          }}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleSubCategoryDelete(subCategory.id); }}
                          style={{ 
                            background: 'transparent', 
                            color: danger, 
                            border: 'none', 
                            borderRadius: '6px', 
                            padding: '6px',
                            cursor: 'pointer',
                            fontSize: '1rem'
                          }}
                          title="Sil"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ürünler */}
        {(selectedMainCategory || selectedSubCategory) && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: dark, margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Ürünler</h2>
              {canEdit() && (
                <button 
                  onClick={() => openProductForm()}
                  style={{ 
                    background: primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>+</span> Ürün Ekle
                </button>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredProducts.map((product, idx) => (
                <div 
                  key={product.id}
                  style={{ 
                    padding: '16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: product.isActive ? 'white' : '#f8fafc',
                    transition: 'all 0.2s ease',
                    opacity: product.isActive ? 1 : 0.5
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: '1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: dark, fontWeight: '600', fontSize: '1rem' }}>{product.nameTr} ({product.nameEn})</span>
                        <span style={{ color: success, fontWeight: 'bold', fontSize: '0.9rem' }}>{product.price}₺</span>
                        {product.isNew && (
                          <span style={{ 
                            color: 'white', 
                            fontWeight: 'bold', 
                            fontSize: '0.75rem',
                            background: secondary,
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>NEW</span>
                        )}
                        {product.allergenInfoTr && (
                          <span style={{ 
                            color: 'white', 
                            fontWeight: 'bold', 
                            fontSize: '0.75rem',
                            background: danger,
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>A</span>
                        )}
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: product.isActive ? success : '#64748b',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: product.isActive ? `${success}20` : '#e5e7eb'
                        }}>
                          {product.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: gray, marginBottom: '4px' }}>
                        {product.descriptionTr} ({product.descriptionEn})
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                        {product.flavorTr} ({product.flavorEn})
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {product.imageUrl && (
                        <img src={product.imageUrl} alt={product.nameTr} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
                      )}
                      {canEdit() && (
                        <>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              onClick={() => moveProduct(product.id, 'up')}
                              disabled={idx === 0}
                              title="Yukarı Taşı"
                              style={{ background: 'transparent', border: '1px solid #e5e7eb', padding: '4px 6px', borderRadius: 6, color: gray }}
                            >⬆️</button>
                            <button
                              onClick={() => moveProduct(product.id, 'down')}
                              disabled={idx === filteredProducts.length - 1}
                              title="Aşağı Taşı"
                              style={{ background: 'transparent', border: '1px solid #e5e7eb', padding: '4px 6px', borderRadius: 6, color: gray }}
                            >⬇️</button>
                          </div>
                          <button 
                            onClick={() => openProductForm(product)}
                            style={{ 
                              background: 'transparent', 
                              color: accent, 
                              border: 'none', 
                              borderRadius: '6px', 
                              padding: '6px',
                              cursor: 'pointer',
                              fontSize: '1rem'
                            }}
                            title="Düzenle"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleProductDelete(product.id)}
                            style={{ 
                              background: 'transparent', 
                              color: danger, 
                              border: 'none', 
                              borderRadius: '6px', 
                              padding: '6px',
                              cursor: 'pointer',
                              fontSize: '1rem'
                            }}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

          {/* MontlyCalendarImage Yönetimi */}
          <div style={{ maxWidth: '1400px', margin: '20px auto', padding: '0 20px' }}>
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 8px 20px -6px rgba(0,0,0,0.15)' }}>
              <div
                onClick={() => setIsMontlyCalendarOpen(o => !o)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: 'clamp(12px, 2.5vw, 18px) clamp(12px, 2.5vw, 20px)', 
                  cursor: 'pointer', 
                  borderBottom: isMontlyCalendarOpen ? '1px solid #eef2f7' : 'none'
                }}
              >
                <h2 style={{ 
                  color: dark, 
                  margin: 0, 
                  fontSize: 'clamp(1rem, 3vw, 1.25rem)', 
                  fontWeight: 700
                }}>
                  Aylık Takvim Görseli
                </h2>
                <span
                  style={{
                    display: 'inline-block', 
                    transform: `rotate(${isMontlyCalendarOpen ? 180 : 0}deg)`,
                    transition: 'transform 0.2s ease', 
                    color: '#64748b', 
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                  }}
                >
                  ▼
                </span>
              </div>
              
              {isMontlyCalendarOpen && (
                <div style={{ padding: 'clamp(12px, 3vw, 20px)' }}>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: window.innerWidth < 768 ? 'stretch' : 'center', 
                    gap: window.innerWidth < 768 ? '12px' : '0',
                    marginBottom: '20px' 
                  }}>
                    {montlyCalendarImages.length === 0 && canEdit() && (
                      <button
                        onClick={() => openMontlyCalendarForm()}
                        style={{
                          background: primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 3vw, 20px)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.9rem)',
                          fontWeight: '500',
                          cursor: 'pointer',
                          minWidth: window.innerWidth < 768 ? '100%' : 'auto'
                        }}
                      >
                        + Görsel Ekle
                      </button>
                    )}
                    {montlyCalendarImages.length > 0 && (
                      <div style={{
                        background: '#fef3c7',
                        color: '#92400e',
                        padding: 'clamp(8px, 2vw, 12px) clamp(12px, 2.5vw, 16px)',
                        borderRadius: '8px',
                        fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                        fontWeight: '500',
                        textAlign: 'center',
                        minWidth: window.innerWidth < 768 ? '100%' : 'auto'
                      }}>
                        ⚠️ Sadece 1 görsel eklenebilir
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: window.innerWidth < 640 ? '1fr' : window.innerWidth < 1024 ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'repeat(auto-fill, minmax(320px, 1fr))', 
                    gap: 'clamp(12px, 2.5vw, 16px)' 
                  }}>
                    {montlyCalendarImages.map((image) => (
                      <div
                        key={image.id}
                        style={{
                          border: '1px solid #e6ebf1',
                          borderRadius: 12,
                          padding: 'clamp(12px, 2.5vw, 16px)',
                          background: '#fff',
                          transition: 'box-shadow .2s ease',
                          minHeight: '280px',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <div style={{ 
                          marginBottom: '12px', 
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img 
                            src={image.imageUrl} 
                            alt="Aylık Takvim" 
                            style={{ 
                              width: '100%', 
                              height: 'clamp(180px, 40vw, 220px)', 
                              objectFit: 'cover', 
                              borderRadius: 8, 
                              border: '1px solid #eef2f7' 
                            }} 
                          />
                        </div>
                        {canEdit() && (
                          <div style={{ 
                            display: 'flex', 
                            gap: '8px',
                            flexDirection: window.innerWidth < 480 ? 'column' : 'row'
                          }}>
                            <button
                              onClick={() => openMontlyCalendarForm(image)}
                              style={{
                                flex: 1,
                                border: '1px solid #e5e7eb',
                                padding: 'clamp(8px, 2.5vw, 10px) clamp(12px, 2.5vw, 16px)',
                                borderRadius: 8,
                                background: '#f8fafc',
                                cursor: 'pointer',
                                fontSize: 'clamp(0.85rem, 2.5vw, 0.9rem)',
                                fontWeight: '500'
                              }}
                            >
                              Düzenle
                            </button>
                            <button
                              onClick={() => deleteMontlyCalendarImage(image.id)}
                              style={{
                                border: '1px solid #fee2e2',
                                color: '#ef4444',
                                padding: 'clamp(8px, 2.5vw, 10px) clamp(12px, 2.5vw, 16px)',
                                borderRadius: 8,
                                background: '#fff',
                                cursor: 'pointer',
                                fontSize: 'clamp(0.85rem, 2.5vw, 0.9rem)',
                                fontWeight: '500'
                              }}
                            >
                              Sil
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {montlyCalendarImages.length === 0 && (
                      <div style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: 'clamp(30px, 6vw, 40px)',
                        color: '#94a3b8',
                        border: '2px dashed #e2e8f0',
                        borderRadius: 12,
                        background: '#f8fafc',
                        fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                      }}>
                        Henüz aylık takvim görseli eklenmemiş
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
      </div>

      {/* BurgerRain Yönetimi */}
      <div style={{ maxWidth: '1400px', margin: '20px auto', padding: '0 20px' }}>
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 8px 20px -6px rgba(0,0,0,0.15)' }}>
          <div
            onClick={() => setIsRainOpen(o => !o)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: 'clamp(12px, 2.5vw, 18px) clamp(12px, 2.5vw, 20px)', cursor: 'pointer', borderBottom: isRainOpen ? '1px solid #eef2f7' : 'none'
            }}
          >
            <h2 style={{ color: dark, margin: 0, fontSize: 'clamp(1rem, 3vw, 1.25rem)', fontWeight: 700 }}>Burger Rain</h2>
            <span
              style={{
                display: 'inline-block', transform: `rotate(${isRainOpen ? 180 : 0}deg)`,
                transition: 'transform 0.2s ease', color: '#64748b', fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
              }}
            >▼</span>
          </div>
          {isRainOpen && (
            <div style={{ padding: 'clamp(12px, 3vw, 20px)' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                  <div
                    key={day}
                    style={{
                      border: '1px solid #e6ebf1', borderRadius: 12, padding: 14,
                      display: 'flex', flexDirection: 'column', gap: 10,
                      background: '#fff', transition: 'box-shadow .2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 700, color: dark }}>{day}.{currentMonth}.{currentYear}</div>
                      {rainImages[day] && (
                        <span style={{ fontSize: 12, color: '#10b981', background: '#ecfdf5', padding: '2px 8px', borderRadius: 999 }}>Kayıtlı</span>
                      )}
                    </div>
                    {rainImages[day] ? (
                      <img src={rainImages[day]} alt={`day-${day}`} style={{ width: '100%', height: 'clamp(80px, 24vw, 120px)', objectFit: 'cover', borderRadius: 8, border: '1px solid #eef2f7' }} />
                    ) : (
                      <div style={{ height: 'clamp(80px, 24vw, 120px)', background: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Görsel yok</div>
                    )}
                    {canEdit() && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <label style={{ flex: 1 }}>
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const url = await uploadRainImage(file);
                            await saveRainImage(day, url);
                          }} />
                          <div style={{ border: '1px solid #e5e7eb', padding: 'clamp(8px, 2.5vw, 10px)', borderRadius: 8, textAlign: 'center', cursor: 'pointer', background: '#f8fafc' }}>Yükle / Değiştir</div>
                        </label>
                        <button onClick={() => saveRainImage(day, null)} style={{ border: '1px solid #fee2e2', color: '#ef4444', padding: 'clamp(8px, 2.5vw, 10px)', borderRadius: 8, background: '#fff' }}>Sil</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ana Kategori Form Modal */}
      {showMainCategoryForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', minWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ color: dark, marginBottom: '20px', fontSize: '1.25rem', fontWeight: '600' }}>
              {editingMainCategory ? 'Ana Kategori Düzenle' : 'Yeni Ana Kategori Ekle'}
            </h3>
            <form onSubmit={handleMainCategorySubmit}>
                              <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: trColor, fontWeight: '500' }}>
                    Türkçe Adı 🇹🇷
                  </label>
                  <input
                    name="nameTr"
                    placeholder="Türkçe Adı"
                    value={mainCategoryForm.nameTr}
                    onChange={(e) => setMainCategoryForm({...mainCategoryForm, nameTr: e.target.value})}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${trColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: trColor
                    }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: enColor, fontWeight: '500' }}>
                    İngilizce Adı EN
                  </label>
                  <input
                    name="nameEn"
                    placeholder="İngilizce Adı"
                    value={mainCategoryForm.nameEn}
                    onChange={(e) => setMainCategoryForm({...mainCategoryForm, nameEn: e.target.value})}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${enColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: enColor
                    }}
                  />
                </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={mainCategoryForm.isActive}
                    onChange={(e) => setMainCategoryForm({...mainCategoryForm, isActive: e.target.checked})}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: dark, fontSize: '0.95rem' }}>Aktif</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="submit" 
                  style={{ 
                    background: primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px 20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    flex: '1'
                  }}
                >
                  {editingMainCategory ? 'Güncelle' : 'Ekle'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowMainCategoryForm(false)}
                  style={{ 
                    background: gray, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px 20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    flex: '1'
                  }}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alt Kategori Form Modal */}
      {showSubCategoryForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', minWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ color: dark, marginBottom: '20px', fontSize: '1.25rem', fontWeight: '600' }}>
              {editingSubCategory ? 'Alt Kategori Düzenle' : 'Yeni Alt Kategori Ekle'}
            </h3>
            <form onSubmit={handleSubCategorySubmit}>
                              <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: trColor, fontWeight: '500' }}>
                    Türkçe Adı 🇹🇷
                  </label>
                  <input
                    name="nameTr"
                    placeholder="Türkçe Adı"
                    value={subCategoryForm.nameTr}
                    onChange={(e) => setSubCategoryForm({...subCategoryForm, nameTr: e.target.value})}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${trColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: trColor
                    }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: enColor, fontWeight: '500' }}>
                    İngilizce Adı EN
                  </label>
                  <input
                    name="nameEn"
                    placeholder="İngilizce Adı"
                    value={subCategoryForm.nameEn}
                    onChange={(e) => setSubCategoryForm({...subCategoryForm, nameEn: e.target.value})}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${enColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: enColor
                    }}
                  />
                </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: dark, fontWeight: '500' }}>
                  Ana Kategori
                </label>
                <select
                  value={subCategoryForm.mainCategoryId}
                  onChange={(e) => setSubCategoryForm({...subCategoryForm, mainCategoryId: e.target.value})}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '1px solid #d1d5db',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                >
                  <option value="">Ana Kategori Seç</option>
                  {mainCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nameTr} ({cat.nameEn})</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={subCategoryForm.isActive}
                    onChange={(e) => setSubCategoryForm({...subCategoryForm, isActive: e.target.checked})}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: dark, fontSize: '0.95rem' }}>Aktif</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="submit" 
                  style={{ 
                    background: primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px 20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    flex: '1'
                  }}
                >
                  {editingSubCategory ? 'Güncelle' : 'Ekle'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowSubCategoryForm(false)}
                  style={{ 
                    background: gray, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px 20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    flex: '1'
                  }}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ürün Form Modal */}
      {showProductForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', minWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ color: dark, marginBottom: '20px', fontSize: '1.25rem', fontWeight: '600' }}>
              {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
            </h3>
            <form onSubmit={handleProductSubmit}>
                              <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: trColor, fontWeight: '500' }}>
                    Türkçe Adı 🇹🇷 (Shift+Enter ile satır)
                  </label>
                  <textarea
                    name="nameTr"
                    placeholder="Türkçe Adı"
                    value={productForm.nameTr}
                    onChange={(e) => setProductForm({...productForm, nameTr: e.target.value})}
                    onKeyDown={handleMultilineKeyDown}
                    rows={2}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${trColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: trColor,
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: enColor, fontWeight: '500' }}>
                    İngilizce Adı EN (Shift+Enter ile satır)
                  </label>
                  <textarea
                    name="nameEn"
                    placeholder="İngilizce Adı"
                    value={productForm.nameEn}
                    onChange={(e) => setProductForm({...productForm, nameEn: e.target.value})}
                    onKeyDown={handleMultilineKeyDown}
                    rows={2}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${enColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: enColor,
                      resize: 'vertical'
                    }}
                  />
                </div>
                              <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: trColor, fontWeight: '500' }}>
                      Türkçe Açıklama 🇹🇷 (Opsiyonel)
                  </label>
                  <input
                    name="descriptionTr"
                      placeholder="Türkçe Açıklama (Opsiyonel)"
                    value={productForm.descriptionTr}
                    onChange={(e) => setProductForm({...productForm, descriptionTr: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${trColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: trColor
                    }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: enColor, fontWeight: '500' }}>
                      İngilizce Açıklama EN (Opsiyonel)
                  </label>
                  <input
                    name="descriptionEn"
                      placeholder="İngilizce Açıklama (Opsiyonel)"
                    value={productForm.descriptionEn}
                    onChange={(e) => setProductForm({...productForm, descriptionEn: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${enColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: enColor
                    }}
                  />
                </div>
                              <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: trColor, fontWeight: '500' }}>
                      Türkçe İçindekiler 🇹🇷 (Opsiyonel)
                  </label>
                  <input
                    name="flavorTr"
                      placeholder="Türkçe İçindekiler (Opsiyonel)"
                    value={productForm.flavorTr}
                    onChange={(e) => setProductForm({...productForm, flavorTr: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${trColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: trColor
                    }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: enColor, fontWeight: '500' }}>
                      İngilizce İçindekiler EN (Opsiyonel)
                  </label>
                  <input
                    name="flavorEn"
                      placeholder="İngilizce İçindekiler (Opsiyonel)"
                    value={productForm.flavorEn}
                    onChange={(e) => setProductForm({...productForm, flavorEn: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: `2px solid ${enColor}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      color: enColor
                    }}
                  />
                </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: dark, fontWeight: '500' }}>
                  Fiyat
                </label>
                <input
                  name="price"
                  placeholder="Fiyat"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '1px solid #d1d5db',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: dark, fontWeight: '500' }}>
                  Ürün Görseli
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const imageUrl = await uploadToStorage(file, 'product-images', 'products');
                        setProductForm(prev => ({ ...prev, imageUrl }));
                      } catch (err) {
                        alert('Görsel yüklenemedi');
                      }
                    }}
                    style={{ 
                      flex: 1,
                    }}
                  />
                  {productForm.imageUrl && (
                    <img src={productForm.imageUrl} alt="preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: dark, fontWeight: '500' }}>
                  Ana Kategori
                </label>
                <select
                  value={productForm.mainCategoryId}
                  onChange={(e) => setProductForm({...productForm, mainCategoryId: e.target.value})}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '1px solid #d1d5db',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                >
                  <option value="">Ana Kategori Seç</option>
                  {mainCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nameTr} ({cat.nameEn})</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: dark, fontWeight: '500' }}>
                  Alt Kategori (Opsiyonel)
                </label>
                <select
                  value={productForm.subCategoryId}
                  onChange={(e) => setProductForm({...productForm, subCategoryId: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '1px solid #d1d5db',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                >
                  <option value="">Alt Kategori (opsiyonel)</option>
                  {subCategories
                    .filter(sub => String(sub.mainCategoryId) === productForm.mainCategoryId)
                    .map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.nameTr} ({sub.nameEn})</option>
                    ))}
                </select>
              </div>
              <div style={{ marginBottom: '20px', display: 'flex', gap: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={productForm.isActive}
                    onChange={(e) => setProductForm({...productForm, isActive: e.target.checked})}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: dark, fontSize: '0.95rem' }}>Aktif</span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: dark, fontSize: '0.95rem', fontWeight: '500' }}>
                    Alerjen Bilgisi (TR)
                  </label>
                  <input
                    type="text"
                    value={productForm.allergenInfoTr}
                    onChange={(e) => setProductForm({...productForm, allergenInfoTr: e.target.value})}
                    placeholder="Örn: Gluten, süt, yumurta"
                    style={{ 
                      padding: '8px 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      fontSize: '0.9rem',
                      width: '100%'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: dark, fontSize: '0.95rem', fontWeight: '500' }}>
                    Alerjen Bilgisi (EN)
                  </label>
                  <input
                    type="text"
                    value={productForm.allergenInfoEn}
                    onChange={(e) => setProductForm({...productForm, allergenInfoEn: e.target.value})}
                    placeholder="Örn: Gluten, milk, egg"
                    style={{ 
                      padding: '8px 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      fontSize: '0.9rem',
                      width: '100%'
                    }}
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={productForm.isNew}
                    onChange={(e) => setProductForm({...productForm, isNew: e.target.checked})}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ color: dark, fontSize: '0.95rem' }}>Yeni mi?</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="submit" 
                  style={{ 
                    background: primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px 20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    flex: '1'
                  }}
                >
                  {editingProduct ? 'Güncelle' : 'Ekle'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowProductForm(false)}
                  style={{ 
                    background: gray, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px 20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    flex: '1'
                  }}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
          )}

          {/* MontlyCalendarImage Form Modal */}
          {showMontlyCalendarForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
              <div style={{ 
                background: 'white', 
                padding: 'clamp(20px, 4vw, 24px)', 
                borderRadius: '12px', 
                minWidth: 'clamp(300px, 90vw, 400px)', 
                maxWidth: '90vw',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' 
              }}>
                <h3 style={{ 
                  color: dark, 
                  marginBottom: '20px', 
                  fontSize: 'clamp(1.1rem, 3vw, 1.25rem)', 
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {editingMontlyCalendar ? 'Aylık Takvim Görseli Düzenle' : 'Yeni Aylık Takvim Görseli Ekle'}
                </h3>
                <form onSubmit={handleMontlyCalendarSubmit}>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      color: dark, 
                      fontWeight: '500',
                      fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                    }}>
                      Görsel Dosyası Seç
                    </label>
                    <input
                      id="montlyCalendarFile"
                      type="file"
                      accept="image/*"
                      required
                      style={{ 
                        width: '100%', 
                        padding: 'clamp(10px, 2.5vw, 12px)', 
                        borderRadius: '8px', 
                        border: '2px solid #d1d5db',
                        fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', 
                        outline: 'none', 
                        transition: 'border-color 0.2s ease',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: 'clamp(0.75rem, 2vw, 0.8rem)', 
                      color: '#6b7280',
                      lineHeight: '1.4'
                    }}>
                      Desteklenen formatlar: JPG, JPEG, PNG, GIF, WEBP<br/>
                      Maksimum dosya boyutu: 5MB
                    </div>
                  </div>
                  
                  {editingMontlyCalendar && (
                    <div style={{ 
                      marginBottom: '20px', 
                      padding: '12px', 
                      background: '#f8fafc', 
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ 
                        fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', 
                        color: '#374151', 
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        Mevcut Görsel:
                      </div>
                      <img 
                        src={editingMontlyCalendar.imageUrl}
                        alt="Mevcut görsel" 
                        style={{ 
                          width: '100%', 
                          height: '120px', 
                          objectFit: 'cover', 
                          borderRadius: '6px',
                          border: '1px solid #d1d5db'
                        }} 
                      />
                    </div>
                  )}

                  <div style={{ 
                    display: 'flex', 
                    gap: '12px',
                    flexDirection: window.innerWidth < 480 ? 'column' : 'row'
                  }}>
                    <button 
                      type="submit" 
                      style={{ 
                        background: primary, 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 3vw, 20px)',
                        fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', 
                        fontWeight: '500', 
                        cursor: 'pointer', 
                        flex: '1',
                        minHeight: '44px'
                      }}
                    >
                      {editingMontlyCalendar ? 'Güncelle' : 'Ekle'}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowMontlyCalendarForm(false);
                        setMontlyCalendarForm({ imageUrl: '' });
                        setEditingMontlyCalendar(null);
                      }}
                      style={{ 
                        background: gray, 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        padding: 'clamp(10px, 2.5vw, 12px) clamp(16px, 3vw, 20px)',
                        fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', 
                        fontWeight: '500', 
                        cursor: 'pointer', 
                        flex: '1',
                        minHeight: '44px'
                      }}
                    >
                      İptal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Kullanıcı Yönetimi Form Modal - Supabase'e taşındı */}
          {/* Kullanıcı Yönetimi - Supabase panelinden yönetilecek */}
          {false && (<></>)}

          {/* Kullanıcı Yönetimi kaldırıldı */}
          {false && (<></>)}
        </>
      )}
    </div>
  );
};

export default MenuAdmin; 